import { useEffect, useRef } from "react";
import type { Instrument } from "../../sdk";
import { Link } from "../../components/Link";
import { CoinMark, Dot } from "../../components/ui";
import { useAssetCtx } from "../../hl/hooks";
import { useMark, useTradingState, useVolumes, type Mark } from "../../state/market";
import { big, changeBp, fromRaw, pctBp, price, ratioBp, scalePlaces, tone, usd } from "../../lib/num";

/** Open interest (asset units, 18 decimals) as dollars at the mark. */
function oiUsd(oi: string, mark: Mark | undefined, scale: string): bigint | null {
  if (!mark) return null;
  return (big(oi) * big(mark.raw)) / big(scale);
}

function OiStat({ side, oi, cap, mark, scale }: { side: "Long" | "Short"; oi: string; cap: string | null; mark: Mark | undefined; scale: string }) {
  const dollars = oiUsd(oi, mark, scale);
  const used = cap ? Number(ratioBp(big(oi), big(cap))) : null;
  return (
    <div className="mb-stat">
      <div className="label">{side} OI</div>
      <div className="num mb-stat-value">{dollars === null ? "—" : usd(dollars, { compact: 4 })}</div>
      {used !== null && (
        <span className="mb-cap" title={`${(used / 100).toFixed(1)}% of the side's cap`}>
          <span className={`mb-cap-fill ${side === "Long" ? "up" : "down"} ${used > 9000 ? "is-full" : ""}`} style={{ width: `${Math.min(100, used / 100)}%` }} />
        </span>
      )}
    </div>
  );
}

function Tab({ instrument, active }: { instrument: Instrument; active: boolean }) {
  const mark = useMark(instrument.id);
  return (
    <Link href={`/trade/${instrument.symbol}`} className="mb-tab" aria-current={active ? "page" : undefined}>
      <CoinMark symbol={instrument.symbol} size={18} />
      <span className="mb-tab-symbol">{instrument.symbol}</span>
      <span className="num mb-tab-price">{mark ? price(mark.raw, instrument.priceScale) : ""}</span>
    </Link>
  );
}

/** The price, ticking toward the colour of its last move. */
function LivePrice({ mark, scale }: { mark: Mark | undefined; scale: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const last = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!mark || !ref.current) return;
    const previous = last.current;
    last.current = mark.raw;
    if (previous === null || previous === mark.raw) return;
    const el = ref.current;
    el.dataset.tick = big(mark.raw) > big(previous) ? "up" : "down";
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => delete el.dataset.tick, 300);
  }, [mark]);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  return (
    <span ref={ref} className="num mb-price">
      {mark ? price(mark.raw, scale) : "—"}
    </span>
  );
}

export function MarketBar({ instrument, instruments }: { instrument: Instrument; instruments: Instrument[] }) {
  const mark = useMark(instrument.id);
  const ctx = useAssetCtx(instrument.symbol);
  const volumes = useVolumes().data;
  const state = useTradingState().data;
  const scale = instrument.priceScale;
  const change = ctx && mark ? changeBp(ctx.prevDayPx, fromRaw(mark.raw, scalePlaces(scale))) : null;

  // the tab says the price
  useEffect(() => {
    if (!mark) return;
    document.title = `${price(mark.raw, scale)} ${instrument.symbol} · papertrade_`;
  }, [mark, scale, instrument.symbol]);
  useEffect(() => () => void (document.title = "papertrade_"), []);

  const banner = state?.tradingPaused
    ? "Trading is paused by the guardian. Closes still settle."
    : state?.pausedActions.includes("openPosition")
      ? "The relayer is not taking new positions right now."
      : instrument.terminal
        ? `${instrument.symbol} is retired: positions settle at its frozen price.`
        : instrument.closeOnly
          ? `${instrument.symbol} is close-only.`
          : null;

  return (
    <div className="mb">
      <nav className="mb-tabs" aria-label="Markets">
        {instruments.map((item) => (
          <Tab key={item.id} instrument={item} active={item.id === instrument.id} />
        ))}
      </nav>
      <div className="mb-main">
        <div className="mb-quote">
          <LivePrice mark={mark} scale={scale} />
          <div className="mb-quote-sub">
            {change !== null && <span className={`num ${tone(change)}`}>{pctBp(change, { sign: true })}</span>}
            <span className="mb-source" title={mark?.source === "chain" ? "Read from the oracle's BBO on HyperEVM" : "Hyperliquid's best bid and ask mid"}>
              <Dot tone={mark?.source === "book" ? "up" : mark ? "warn" : "off"} />
              {mark?.source === "chain" ? "oracle" : mark?.source === "frozen" ? "frozen" : "mid"}
            </span>
          </div>
        </div>
        <div className="mb-stats">
          <div className="mb-stat">
            <div className="label">24h volume</div>
            <div className="num mb-stat-value">{volumes?.[instrument.id] ? usd(volumes[instrument.id] as string, { compact: 4 }) : "—"}</div>
          </div>
          <OiStat side="Long" oi={instrument.longOi} cap={instrument.longCap} mark={mark} scale={scale} />
          <OiStat side="Short" oi={instrument.shortOi} cap={instrument.shortCap} mark={mark} scale={scale} />
          <div className="mb-stat">
            <div className="label">Max leverage</div>
            <div className="num mb-stat-value">{instrument.maxLeverage}×</div>
          </div>
          <div className="mb-stat mb-stat-wide">
            <div className="label">Bid · Ask</div>
            <div className="num mb-stat-value">
              {mark?.bid && mark.ask ? `${price(mark.bid, scale)} · ${price(mark.ask, scale)}` : "—"}
            </div>
          </div>
          <div className="mb-stat">
            <div className="label">Funding</div>
            <div className="num mb-stat-value">none</div>
          </div>
        </div>
      </div>
      {banner && <div className="mb-banner">{banner}</div>}
    </div>
  );
}
